import { enGB } from '../quote/locales/en-GB';
import { plPL } from '../quote/locales/pl-PL';
import type {
  Locale,
  Market,
  QuoteInput,
  QuoteResult,
  Region,
  RoomInput,
  RoomType,
  Travel
} from '../quote/types';

export type PropertyType = 'house' | 'flat' | 'commercial';
export type Step = 0 | 1 | 2 | 3 | 4;
export type ErrorMap = Map<string, string>;

export interface AppState {
  locale: Locale;
  market: Market;
  region: Region;
  travel: Travel;
  propertyType: PropertyType;
  step: Step;
  rooms: RoomInput[];
  result: QuoteResult | null;
  errors: ErrorMap;
}

export const roomTypes: RoomType[] = ['livingRoom', 'bedroom', 'kitchen', 'bathroom', 'hallway', 'office', 'other'];
export const stepKeys = ['project', 'dimensions', 'surfaces', 'condition', 'finish'] as const;

export function newId(): string {
  return `room-${crypto.randomUUID?.() ?? Math.random().toString(36).slice(2)}`;
}

export function createRoom(index = 0): RoomInput {
  return {
    id: newId(),
    type: index === 0 ? 'livingRoom' : 'bedroom',
    customName: '',
    length: 4,
    width: 3.5,
    height: 2.4,
    doors: 1,
    windows: 1,
    radiators: 1,
    paintWalls: true,
    paintCeiling: true,
    paintSkirting: false,
    paintedDoors: 0,
    paintedRadiators: 0,
    condition: 'good',
    colourChange: 'similar',
    materialStandard: 'standard',
    wallpaperRemoval: false,
    stainBlocking: false,
    highCeiling: false,
    difficultAccess: false,
    furnished: false,
    issues: {
      moistureOrMould: false,
      unstableSurface: false,
      scaffoldingRequired: false,
      specialistFinish: false,
      fullReplastering: false,
      exteriorWork: false
    }
  };
}

export const state: AppState = {
  locale: 'en-GB',
  market: 'UK',
  region: 'standard',
  travel: 'local',
  propertyType: 'house',
  step: 0,
  rooms: [createRoom()],
  result: null,
  errors: new Map()
};

export const escapeHtml = (value: string): string =>
  value.replace(/[&<>'"]/g, (character) => ({
    '&': '&amp;',
    '<': '&lt;',
    '>': '&gt;',
    "'": '&#39;',
    '"': '&quot;'
  })[character] ?? character);

export const numberValue = (value: string): number => {
  const normalized = value.trim().replace(',', '.');
  if (!normalized || /e/i.test(normalized)) return Number.NaN;
  return Number(normalized);
};

export function copy() {
  return state.locale === 'en-GB' ? enGB : plPL;
}

export function roomLabel(room: RoomInput, index: number): string {
  const c = copy();
  if (room.customName.trim()) return room.customName.trim();
  const key = `type${room.type.charAt(0).toUpperCase()}${room.type.slice(1)}` as keyof typeof c;
  const label = typeof c[key] === 'string' ? c[key] : c.room;
  return `${label} ${index + 1}`;
}

export function quoteInput(): QuoteInput {
  return {
    market: state.market,
    locale: state.locale,
    region: state.region,
    travel: state.travel,
    rooms: state.rooms
  };
}

export function option(value: string, label: string, selected: boolean): string {
  return `<option value="${escapeHtml(value)}"${selected ? ' selected' : ''}>${escapeHtml(label)}</option>`;
}

export function radioCard(name: string, value: string, label: string, description: string, checked: boolean, attrs = ''): string {
  return `
    <label class="choice-card">
      <input type="radio" name="${escapeHtml(name)}" value="${escapeHtml(value)}" ${checked ? 'checked' : ''} ${attrs} />
      <span class="choice-indicator" aria-hidden="true"></span>
      <span class="choice-copy"><strong>${escapeHtml(label)}</strong>${description ? `<small>${escapeHtml(description)}</small>` : ''}</span>
    </label>`;
}

export function checkCard(label: string, checked: boolean, attrs: string): string {
  return `
    <label class="check-card">
      <input type="checkbox" ${checked ? 'checked' : ''} ${attrs} />
      <span class="check-box" aria-hidden="true">✓</span>
      <span>${escapeHtml(label)}</span>
    </label>`;
}

export function errorFor(roomId: string, field: string): string {
  const message = state.errors.get(`${roomId}:${field}`);
  return message ? `<p class="field-error" id="error-${roomId}-${field}">${escapeHtml(message)}</p>` : '';
}

export function numericField(room: RoomInput, field: keyof RoomInput, label: string, min: number, max: number, step: number, suffix = ''): string {
  const value = room[field];
  const error = state.errors.get(`${room.id}:${String(field)}`);
  return `
    <div class="field ${error ? 'has-error' : ''}">
      <label for="${room.id}-${String(field)}">${escapeHtml(label)}</label>
      <div class="input-with-suffix">
        <input id="${room.id}-${String(field)}" type="text" inputmode="decimal" value="${String(value)}"
          data-room-id="${room.id}" data-field="${String(field)}" aria-invalid="${Boolean(error)}"
          ${error ? `aria-describedby="error-${room.id}-${String(field)}"` : ''}
          data-min="${min}" data-max="${max}" data-step="${step}" />
        ${suffix ? `<span>${escapeHtml(suffix)}</span>` : ''}
      </div>
      ${errorFor(room.id, String(field))}
    </div>`;
}

export function countField(room: RoomInput, field: keyof RoomInput, label: string, max: number): string {
  const value = room[field];
  const error = state.errors.get(`${room.id}:${String(field)}`);
  return `
    <div class="field ${error ? 'has-error' : ''}">
      <label for="${room.id}-${String(field)}">${escapeHtml(label)}</label>
      <input id="${room.id}-${String(field)}" type="number" min="0" max="${max}" step="1" value="${String(value)}"
        data-room-id="${room.id}" data-field="${String(field)}" aria-invalid="${Boolean(error)}"
        ${error ? `aria-describedby="error-${room.id}-${String(field)}"` : ''} />
      ${errorFor(room.id, String(field))}
    </div>`;
}
